import { useMutation } from '@tanstack/react-query';
import {
  MonitoringAlertDto,
  IntruderEventData,
  updateIntruderDetails,
} from '../alertApi';
import { AdminOffenderCard } from './AdminOffenderCard';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from '@/hooks/use-toast';
import { MonitorAlertFullDto } from '@/app/Alert/types';

interface IntruderDetailsSectionProps {
  alert: MonitorAlertFullDto;
  onSaved?: () => void;
}

const intruderSchema = z.object({
  policeId: z.string().optional(),
  weapons: z.string().optional(),
  closureResult: z.string().optional(),
  policeResponded: z.boolean().optional(),
  offenceType: z.string().optional(),
});

type IntruderFormValues = z.infer<typeof intruderSchema>;

export function IntruderDetailsSection({ alert, onSaved }: IntruderDetailsSectionProps) {
  const intruder = alert.intruderEventData;

  const form = useForm<IntruderFormValues>({
    resolver: zodResolver(intruderSchema),
    defaultValues: {
      policeId: intruder?.policeId || '',
      weapons: intruder?.weapons || '',
      closureResult: intruder?.closureResult || '',
      policeResponded: intruder?.policeResponded || false,
      offenceType: intruder?.offenceType || '',
    },
  });

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: IntruderEventData) => updateIntruderDetails(alert.id!, data),
    onSuccess: () => {
      toast({ title: 'Intruder details saved' });
      onSaved?.();
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Failed to save intruder details' });
    },
  });

  const onSubmit = form.handleSubmit(async (data) => {
    await mutateAsync(data);
    form.reset(data);
  });

  if (!intruder) {
    return null;
  }

  const intruders = intruder.intruders ?? [];

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="policeId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Police Id</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="weapons"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Weapons</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="offenceType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Offence Type</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="closureResult"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>Resolution Notes</FormLabel>
                <FormControl>
                  <Textarea {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="policeResponded"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Police responded</FormLabel>
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {intruders.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Offender Details</h3>
            <div className="grid grid-cols-1 gap-4 ">
              {intruders.map((profile, index) => (
                <AdminOffenderCard
                  key={index}
                  offender={profile}
                  offenderID={(index + 1).toString()}
                  alertData={alert as any}
                />
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-end">
          <Button type="submit" className="min-w-48" disabled={isPending || !form.formState.isDirty}>
            {isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
